import { create } from 'zustand';
import { streamAwareInterval } from '@/lib/liveInvalidation';

/**
 * Connection-state store for the Session-event SSE stream (C3.2).
 *
 * Polling hooks read this store to compute their `refetchInterval`:
 * when `status === 'open'` and the Api's live bus is running (`live`,
 * from the stream's connected ack), push covers the screen and nothing
 * polls (live cards, 28 Oct 2026); open without the live bus, polling
 * backs off to a 60s heartbeat; otherwise polling stays at its
 * baseline (4s on ChargingPage, 15s in useActiveSession, 10s on
 * SessionDetailPage when active).
 *
 * Lifecycle is owned entirely by `apps/web/src/lib/sessionEventStream.ts`:
 * only that module calls `setStatus` and `setLive`. Consumers are
 * read-only via `useStreamAwareInterval`.
 *
 * Why a separate store from useAuthStore: the auth store persists
 * to localStorage via Zustand's persist middleware and carries
 * sensitive fields. The stream-status store is ephemeral, has no
 * sensitive payload, and shouldn't roundtrip through localStorage
 * on every status change. Kept separate so the boundaries stay
 * clean.
 */

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'error';

interface SessionStreamState {
  status: StreamStatus;
  /** The Api said its live bus is running on this connection: `live`
   *  events (own sessions, payouts, points, charger availability) will
   *  arrive, so live screens need no poll. False until a connected ack
   *  says otherwise, and again whenever the stream drops. */
  live: boolean;
  /** Wall-clock of the most recent `'open'` transition. Useful for
   *  UI affordances ("connected 12s ago") if any future surface
   *  wants to display it. Currently unused by any hook. */
  lastConnectedAt: number | null;
  setStatus: (status: StreamStatus) => void;
  setLive: (live: boolean) => void;
}

export const useSessionStreamStore = create<SessionStreamState>((set) => ({
  status: 'idle',
  live: false,
  lastConnectedAt: null,
  setStatus: (status) =>
    set((state) => ({
      status,
      live: status === 'open' ? state.live : false,
      lastConnectedAt: status === 'open' ? Date.now() : state.lastConnectedAt,
    })),
  setLive: (live) => set({ live }),
}));

/**
 * Polling-aware interval: false (no poll) when the stream is open and
 * the live bus is running; 60s when open without it (push covers state
 * changes, polling is a heartbeat); the consumer's baseline `fallbackMs`
 * otherwise (stream unavailable, polling carries correctness at its
 * original cadence).
 *
 * Reactive: when stream status flips, React re-renders the consumer
 * and React Query picks up the new interval on its next tick.
 */
export function useStreamAwareInterval(fallbackMs: number): number | false {
  const status = useSessionStreamStore((s) => s.status);
  const live = useSessionStreamStore((s) => s.live);
  return streamAwareInterval(status, live, fallbackMs);
}
